import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ComprovacaoFinanceiroPorContrato {
  contrato_id: string;
  valor_aprovado_total: number;
  valor_comprovado_total: number;
  gap_comprovacao: number;
}

/**
 * Agrega valores de aprovação × comprovação por contrato a partir da view
 * existente `vw_net_comprovacao_boletim`. Não cria nova view nem novo endpoint:
 * apenas consome a fonte de boletins já existente e agrega client-side.
 *
 * Usado no header executivo da IFMedicoesPage para o KPI "Valor aprovado sem
 * comprovante de pagamento registrado".
 */
export function useComprovacaoFinanceiroResumo(contratoIds: string[]) {
  return useQuery({
    queryKey: ['net-comprovacao-financeiro-resumo', ...contratoIds.sort()],
    queryFn: async () => {
      if (contratoIds.length === 0) {
        return {} as Record<string, ComprovacaoFinanceiroPorContrato>;
      }
      const { data, error } = await supabase
        .from('vw_net_comprovacao_boletim' as any)
        .select('contrato_id, valor_aprovado, valor_comprovado')
        .in('contrato_id', contratoIds);

      if (error) {
        console.error('[useComprovacaoFinanceiroResumo]', error);
        return {} as Record<string, ComprovacaoFinanceiroPorContrato>;
      }

      const map: Record<string, ComprovacaoFinanceiroPorContrato> = {};
      for (const row of (data || []) as any[]) {
        const id = row.contrato_id as string;
        if (!map[id]) {
          map[id] = {
            contrato_id: id,
            valor_aprovado_total: 0,
            valor_comprovado_total: 0,
            gap_comprovacao: 0,
          };
        }
        map[id].valor_aprovado_total += Number(row.valor_aprovado || 0);
        map[id].valor_comprovado_total += Number(row.valor_comprovado || 0);
      }
      for (const id of Object.keys(map)) {
        map[id].gap_comprovacao =
          map[id].valor_aprovado_total - map[id].valor_comprovado_total;
      }
      return map;
    },
    enabled: contratoIds.length > 0,
    staleTime: 60 * 1000,
  });
}
